/**
 * Fecha estable de inicio de deuda ("desde cuándo debe").
 *
 * `customers.updated_at` se pisa con cualquier edición de perfil — no puede
 * ser la referencia de mora (bug auditado: editar al cliente lo sacaba de
 * "Vencidos"). En su lugar reconstruimos la cuenta sobre el snapshot en RAM:
 *
 * - Los pagos asignados (`sale_payment_allocations` no anuladas) cubren el
 *   cargo de su venta.
 * - Los pagos sin asignar cubren los cargos más viejos primero (FIFO).
 * - `debtSince` = `created_at` del cargo más viejo que quedó impago.
 *
 * Limitación conocida: el snapshot trae como máximo 1000 movimientos /
 * 1000 allocations (los más recientes). Si el cargo impago quedó fuera de la
 * ventana, no hay `debtSince` y el caller cae a `updated_at` (comportamiento
 * anterior, no peor).
 */

export type DebtSinceMovementInput = {
  id?: string | null;
  customer_id: string | null;
  type: string | null;
  amount: number | string | null;
  sale_id: string | null;
  created_at: string | null;
};

export type DebtSinceAllocationInput = {
  sale_id: string | null;
  movement_id?: string | null;
  amount: number | string | null;
  voided_at?: string | null;
};

type DebtSinceCharge = {
  id: string;
  amount: number;
  saleId: string | null;
  createdAt: string;
};

const EPSILON = 0.009;

function toAmount(value: number | string | null | undefined): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * customer_id → ISO `created_at` del cargo más viejo aún impago.
 * Cliente sin cargo impago (o sin movimientos en el snapshot) → sin entrada.
 */
export function buildDebtSinceByCustomerId(
  movements: ReadonlyArray<DebtSinceMovementInput>,
  allocations: ReadonlyArray<DebtSinceAllocationInput>,
): Map<string, string> {
  const allocatedBySale = new Map<string, number>();
  const allocatedByPayment = new Map<string, number>();
  for (const alloc of allocations) {
    if (!alloc || alloc.voided_at) continue;
    const amount = toAmount(alloc.amount);
    if (amount <= 0) continue;
    const saleId = alloc.sale_id ? String(alloc.sale_id) : '';
    if (saleId) {
      allocatedBySale.set(saleId, (allocatedBySale.get(saleId) ?? 0) + amount);
    }
    const paymentId = alloc.movement_id ? String(alloc.movement_id) : '';
    if (paymentId) {
      allocatedByPayment.set(paymentId, (allocatedByPayment.get(paymentId) ?? 0) + amount);
    }
  }

  const chargesByCustomer = new Map<string, DebtSinceCharge[]>();
  const unallocatedPoolByCustomer = new Map<string, number>();

  for (const movement of movements) {
    const customerId = movement?.customer_id ? String(movement.customer_id) : '';
    if (!customerId) continue;
    const amount = toAmount(movement.amount);
    if (amount <= 0) continue;
    if (movement.type === 'charge') {
      const createdAt = movement.created_at ? String(movement.created_at) : '';
      if (!createdAt) continue;
      const list = chargesByCustomer.get(customerId) ?? [];
      list.push({
        id: movement.id ? String(movement.id) : '',
        amount,
        saleId: movement.sale_id ? String(movement.sale_id) : null,
        createdAt,
      });
      chargesByCustomer.set(customerId, list);
    } else if (movement.type === 'payment') {
      const allocated = movement.id ? allocatedByPayment.get(String(movement.id)) ?? 0 : 0;
      const free = Math.max(0, amount - allocated);
      unallocatedPoolByCustomer.set(
        customerId,
        (unallocatedPoolByCustomer.get(customerId) ?? 0) + free,
      );
    }
  }

  const result = new Map<string, string>();
  for (const [customerId, charges] of chargesByCustomer) {
    // FIFO: los pagos sin venta asignada cubren primero la deuda más vieja.
    charges.sort((a, b) =>
      a.createdAt === b.createdAt ? a.id.localeCompare(b.id) : a.createdAt.localeCompare(b.createdAt),
    );
    let pool = unallocatedPoolByCustomer.get(customerId) ?? 0;
    for (const charge of charges) {
      const allocated = charge.saleId ? allocatedBySale.get(charge.saleId) ?? 0 : 0;
      let outstanding = Math.max(0, charge.amount - allocated);
      const covered = Math.min(pool, outstanding);
      pool -= covered;
      outstanding -= covered;
      if (outstanding > EPSILON) {
        result.set(customerId, charge.createdAt);
        break;
      }
    }
  }
  return result;
}

/** Wrapper para el payload de `get_mobile_customers_snapshot` en RAM. */
export function buildDebtSinceByCustomerIdFromSnapshot(snapshot: {
  customer_movements?: ReadonlyArray<DebtSinceMovementInput> | null;
  sale_payment_allocations?: ReadonlyArray<DebtSinceAllocationInput> | null;
} | null | undefined): Map<string, string> {
  if (!snapshot) return new Map();
  return buildDebtSinceByCustomerId(
    snapshot.customer_movements ?? [],
    snapshot.sale_payment_allocations ?? [],
  );
}
